/**
 * pnlDataSource.ts — WatermelonDB → ProfitLossInput bridge.
 *
 * Three basis notes:
 *  - Sales & COGS are on the **accrual** basis: invoices raised in the range,
 *    and the cost snapshot on their lines. This matches how revenue is earned,
 *    not when it is collected.
 *  - Expenses are selected by `expense_date` (the indexed business date), not
 *    `created_at` — a back-dated expense belongs to the day it was incurred, the
 *    same rule Day Close was corrected to (§2.4).
 *  - Voided invoices are excluded so a cancelled sale carries neither its
 *    revenue nor its COGS.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Invoice, LineItem, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { Expense, ExpenseCategory } from '@/db/models/System';
import type { ProfitLossInput } from './profitAndLoss';

/** Load a store's P&L inputs for the half-open range `[from, to)`. */
export async function loadProfitAndLoss(
  storeId: string,
  from: Date,
  to: Date,
): Promise<ProfitLossInput> {
  const fromMs = from.getTime();
  const toMs = to.getTime();

  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    Q.where('type', Q.oneOf(['SALE', 'CREDIT_NOTE'])),
    Q.where('created_at', Q.gte(fromMs)),
    Q.where('created_at', Q.lt(toMs)),
  ).fetch();

  const invoiceIds = invoices.map((i) => i.id);
  const typeByInvoiceId = new Map(invoices.map((i) => [i.id, i.type]));

  const lineRows = await fetchInChunks(invoiceIds, (batch) =>
    database.get<LineItem>('line_items').query(
      Q.where('invoice_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );

  // ── Expenses dated in the range (by business date), scoped to the store ──
  const expenseRows = await database.get<Expense>('expenses').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('expense_date', Q.gte(fromMs)),
    Q.where('expense_date', Q.lt(toMs)),
  ).fetch();

  // Resolve category names; an expense with a missing/deleted category still
  // counts toward the total under a fallback label.
  const categoryIds = [...new Set(expenseRows.map((e) => e.categoryId))];
  const categories = await fetchInChunks(categoryIds, (batch) =>
    database.get<ExpenseCategory>('expense_categories').query(
      Q.where('id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );
  const categoryName = new Map(categories.map((c) => [c.id, c.name]));

  return {
    invoices: invoices.map((i) => ({
      type: i.type,
      totalAmount: i.totalAmount,
      totalTax: i.totalTax,
    })),
    lines: lineRows.map((li) => ({
      quantity: li.quantity,
      totalAmount: li.totalAmount,
      costPrice: li.costPrice,
      invoiceType: typeByInvoiceId.get(li.invoiceId) ?? 'SALE',
    })),
    expenses: expenseRows.map((e) => ({
      amount: e.amount,
      categoryName: categoryName.get(e.categoryId) ?? 'Uncategorised',
    })),
  };
}
